import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Target, CalendarDays, Gift, Medal, Crown, Sparkles, Plane, Flame, ArrowRight, Share2, Gem, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime, fileUrl } from "../../lib/api";
import { LoadingRows } from "../../components/Shared";

const CSS = `
@keyframes ch-grad { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
@keyframes ch-float { 0%,100% { transform: translateY(0) rotate(-4deg); } 50% { transform: translateY(-10px) rotate(5deg); } }
@keyframes ch-confetti { 0% { transform: translateY(-8%) rotate(0); opacity: 0; } 8% { opacity: 1; } 100% { transform: translateY(105vh) rotate(540deg); opacity: 0; } }
@keyframes ch-glowbar { 0%,100% { filter: drop-shadow(0 0 3px rgba(245,183,0,.5)); } 50% { filter: drop-shadow(0 0 9px rgba(245,183,0,.8)); } }
@keyframes ch-bar { from { width: 0; } }
.ch-hero-grad { background: linear-gradient(115deg, #007A4D 0%, #2563EB 35%, #06B6D4 62%, #7C3AED 85%, #F5B700 125%); background-size: 220% 220%; animation: ch-grad 16s ease infinite; }
.ch-card { border-radius: 24px; background: rgba(255,255,255,.88); backdrop-filter: blur(10px); border: 1px solid rgba(15,23,42,.07); box-shadow: 0 12px 32px -14px rgba(15,23,42,.16); transition: transform .25s ease, box-shadow .25s ease; }
.ch-card:hover { transform: translateY(-3px); box-shadow: 0 20px 44px -16px rgba(15,23,42,.22); }
.ch-fill { background: linear-gradient(90deg, #007A4D, #F5B700); animation: ch-bar 1s ease-out, ch-glowbar 2.6s ease-in-out infinite 1s; }
.ch-confetti { position: absolute; width: 8px; height: 13px; top: -14px; border-radius: 2px; animation: ch-confetti 3s linear infinite; opacity: 0; }
@media (prefers-reduced-motion: reduce) { .ch-hero-grad, .ch-fill, .ch-confetti, .ch-anim { animation: none !important; } }
`;

const TIER = {
  bronze: { bg: "#FFF0E0", tx: "#8b5423", bd: "#CD7F32" },
  silver: { bg: "#F7F9FC", tx: "#475569", bd: "#AEB7C4" },
  gold: { bg: "#FFF8E1", tx: "#b8860b", bd: "#F5B700" },
  platinum: { bg: "#ECFEFF", tx: "#0e7490", bd: "#06B6D4" },
};
const REWARD_ICON = { vacation: Plane, travel: Plane, gift: Gift, gold_medal: Medal, cash: CircleDollarSign, custom: Gem };
const CARD_GRADS = ["linear-gradient(135deg,#007A4D,#06B6D4)", "linear-gradient(135deg,#2563EB,#7C3AED)", "linear-gradient(135deg,#F97316,#F5B700)", "linear-gradient(135deg,#DB2777,#7C3AED)"];

function Progress({ pct }) {
  return (
    <div className="h-3.5 rounded-full bg-zinc-100 overflow-hidden">
      <div data-testid="challenge-progress-fill" className="ch-fill h-full rounded-full" style={{ width: `${pct}%` }} />
    </div>
  );
}

function ChallengeCard({ c, live, onShare, idx = 0 }) {
  const RI = REWARD_ICON[c.reward_type] || Gem;
  return (
    <div data-testid={`challenge-${c.id}`} className={`ch-card overflow-hidden ${c.completed ? "ring-2" : ""}`} style={c.completed ? { "--tw-ring-color": "#F5B700" } : {}}>
      {c.banner ? (
        <img src={fileUrl(c.banner)} alt="" className="w-full h-36 object-cover" />
      ) : (
        <div className="h-24 relative overflow-hidden" style={{ background: CARD_GRADS[idx % CARD_GRADS.length] }}>
          <Trophy className="absolute right-5 top-1/2 -translate-y-1/2 h-12 w-12 text-white/25" />
          <span className="absolute left-5 top-1/2 -translate-y-1/2 text-white font-display font-extrabold text-sm tracking-widest uppercase opacity-90">{c.reward_type?.replace(/_/g, " ") || "challenge"}</span>
        </div>
      )}
      <div className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-display font-extrabold text-xl tracking-tight text-zinc-900">{c.name}</p>
            {c.description && <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{c.description}</p>}
          </div>
          <span className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider ${live ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{live ? "🔥 Live" : c.status}</span>
        </div>

        {c.completed ? (
          <div data-testid={`challenge-done-${c.id}`} className="relative overflow-hidden rounded-2xl p-5 text-center space-y-2" style={{ background: "linear-gradient(135deg,#FFF8E1,#FFF3A3)" }}>
            {["#F5B700", "#007A4D", "#2563EB", "#DB2777"].flatMap((col, ci) =>
              [15, 35, 55, 75].map((l, i) => <span key={`${ci}-${i}`} className="ch-confetti" style={{ background: col, left: `${l}%`, animationDelay: `${(ci * 4 + i) * 0.25}s` }} />))}
            <Crown className="h-8 w-8 mx-auto" style={{ color: "#F5B700" }} />
            <p className="font-display font-extrabold text-lg" style={{ color: "#b8860b" }}>🎉 TARGET COMPLETED!</p>
            <p className="text-sm text-zinc-700">Congratulations! You completed <b>{c.name}</b>.</p>
            <div className="flex items-center justify-center gap-3 text-xs font-bold text-zinc-600">
              <span>Target: {fmtINR(c.target)}</span>·<span>Completed: {fmtINR(c.earned)}</span>
            </div>
            {c.achievement_tier && (
              <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-extrabold uppercase"
                style={{ background: TIER[c.achievement_tier]?.bg, color: TIER[c.achievement_tier]?.tx, borderColor: TIER[c.achievement_tier]?.bd }}>
                <Medal className="h-3.5 w-3.5" /> {c.achievement_tier} Achiever
              </span>
            )}
            {c.reward_description && (
              <p className="text-xs font-bold text-zinc-700 flex items-center justify-center gap-1.5"><RI className="h-3.5 w-3.5" /> Reward: {c.reward_description}</p>
            )}
            <p className="text-[11px] font-bold text-amber-700 bg-amber-100/70 rounded-full px-3 py-1 inline-block">Reward Pending Verification</p>
            <button data-testid={`share-${c.id}`} onClick={() => onShare(c)}
              className="mt-1 inline-flex items-center gap-2 rounded-full px-5 h-9 text-xs font-extrabold text-white" style={{ background: "linear-gradient(120deg,#007A4D,#2563EB)" }}>
              <Share2 className="h-3.5 w-3.5" /> Share Achievement
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            <div className="flex justify-between items-end">
              <p className="font-display font-extrabold text-lg text-zinc-900">{fmtINR(c.earned)} <span className="text-sm font-bold text-zinc-400">/ {fmtINR(c.target)}</span></p>
              <p className="text-sm font-extrabold" style={{ color: "#007A4D" }}>{c.pct}% COMPLETE</p>
            </div>
            <Progress pct={c.pct} />
            <p className="text-xs text-zinc-500">Remaining: <b className="text-zinc-800">{fmtINR(Math.max(0, c.target - c.earned))}</b></p>
          </div>
        )}

        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-zinc-500">
          {c.reward_description && !c.completed && <span className="inline-flex items-center gap-1.5 font-semibold text-zinc-700"><RI className="h-3.5 w-3.5" style={{ color: "#F5B700" }} /> {c.reward_description}</span>}
          {c.days_left != null && live && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {c.days_left >= 0 ? `${c.days_left} days left` : "Ending soon"}</span>}
          {c.start_date && <span className="inline-flex items-center gap-1.5"><Target className="h-3.5 w-3.5" /> {fmtDateTime(c.start_date).split(",")[0]} → {c.end_date ? fmtDateTime(c.end_date).split(",")[0] : "open"}</span>}
        </div>
      </div>
    </div>
  );
}

export default function PartnerChallenges() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/partner/challenges").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/partner/leaderboard?period=month").then((r) => setBoard(r.data.top || [])).catch(() => setBoard([]));
  }, []);

  const summary = useMemo(() => {
    if (!data) return null;
    const all = [...data.live, ...data.upcoming, ...data.past];
    return {
      active: data.live.length,
      completed: data.achievements.length,
      earned: all.reduce((a, c) => a + (c.earned || 0), 0),
      rewards: data.achievements.length,
    };
  }, [data]);

  const rewards = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    [...data.live, ...data.upcoming, ...data.past].forEach((c) => {
      const key = c.reward_description || c.reward_type;
      if (key && !seen.has(key)) seen.set(key, { name: c.reward_description || c.reward_type, type: c.reward_type, target: c.target, image: c.reward_image, status: c.status });
    });
    return [...seen.values()];
  }, [data]);

  const share = async (c) => {
    const text = `🏆 CHALLENGE COMPLETED! I completed the ${c.name} (${fmtINR(c.target)} target) on MILTROO! Earn More. Build More. Grow More. Start. Sell. Grow. Anywhere.`;
    if (navigator.share) { try { await navigator.share({ title: "MILTROO Achievement", text }); return; } catch {} }
    try { await navigator.clipboard.writeText(text); toast.success("Achievement copied — paste it anywhere!"); } catch { toast.error("Share not supported"); }
  };

  const MEDAL = { 1: "#F5B700", 2: "#AEB7C4", 3: "#CD7F32" };

  return (
    <div data-testid="challenges-page" className="space-y-10">
      <style>{CSS}</style>

      {/* HERO */}
      <section className="ch-hero-grad relative overflow-hidden rounded-[28px] text-white">
        <div className="absolute -top-16 -right-10 h-60 w-60 rounded-full opacity-25" style={{ background: "radial-gradient(circle, #F5B700, transparent 70%)" }} />
        <div className="absolute -bottom-16 -left-10 h-52 w-52 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #06B6D4, transparent 70%)" }} />
        <Trophy className="ch-anim absolute right-6 top-6 h-14 w-14 text-yellow-300/80 hidden sm:block" style={{ animation: "ch-float 5s ease-in-out infinite" }} />
        <Plane className="ch-anim absolute right-28 bottom-8 h-9 w-9 text-white/25 hidden sm:block" style={{ animation: "ch-float 6.5s ease-in-out infinite .8s" }} />
        <Medal className="ch-anim absolute left-8 bottom-6 h-9 w-9 text-white/25 hidden sm:block" style={{ animation: "ch-float 6s ease-in-out infinite .4s" }} />
        <div className="relative px-6 py-10 sm:px-12 sm:py-14 max-w-3xl">
          <p className="text-[11px] font-extrabold tracking-[0.25em] uppercase text-emerald-100/80 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Earn • Achieve • Travel</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[1.05]">Earning Challenges</h1>
          <p className="mt-3 text-sm sm:text-base text-white/80 max-w-xl">Reach your earning targets and unlock exciting rewards, travel experiences, gifts and achievements.</p>
        </div>
      </section>

      {/* SUMMARY CARDS */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="challenge-summary">
          {[
            { l: "Active Challenges", v: summary.active, g: "linear-gradient(135deg,#F97316,#DB2777)", i: Flame, t: `${summary.active} Active` },
            { l: "Completed", v: summary.completed, g: "linear-gradient(135deg,#007A4D,#00A86B)", i: Trophy, t: `${summary.completed} Completed` },
            { l: "Challenge Earnings", v: fmtINR(summary.earned), g: "linear-gradient(135deg,#2563EB,#06B6D4)", i: CircleDollarSign, t: fmtINR(summary.earned) },
            { l: "Rewards Unlocked", v: summary.rewards, g: "linear-gradient(135deg,#F5B700,#F97316)", i: Gift, t: `${summary.rewards} Rewards` },
          ].map((s) => (
            <div key={s.l} data-testid={`sum-${s.l.toLowerCase().replace(/\s/g, "-")}`} className="rounded-[22px] p-5 text-white" style={{ background: s.g }}>
              <s.i className="h-6 w-6 opacity-85" />
              <p className="font-display font-extrabold text-2xl sm:text-3xl mt-2">{s.v}</p>
              <p className="text-[11px] font-bold uppercase tracking-widest opacity-85 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      {!data ? <div className="ch-card p-8"><LoadingRows rows={3} /></div> : (
        <>
          {/* LIVE */}
          <section>
            <h2 className="font-display font-extrabold text-2xl tracking-tight mb-5 flex items-center gap-2 text-zinc-900 dark:text-white"><Flame className="h-6 w-6 text-orange-500" /> LIVE EARNING CHALLENGES</h2>
            {data.live.length === 0 ? (
              <div className="ch-card p-10 text-center">
                <Trophy className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
                <p className="font-display font-bold text-lg text-zinc-900">No live challenges right now</p>
                <p className="text-sm text-zinc-500 mt-1">New challenges from the MILTROO team will appear here — stay tuned!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">{data.live.map((c, i) => <ChallengeCard key={c.id} c={c} live onShare={share} idx={i} />)}</div>
            )}
          </section>

          {/* TRAVEL REWARD */}
          <section className="relative overflow-hidden rounded-[28px] text-white" style={{ background: "linear-gradient(115deg, #0e7490, #2563EB 55%, #7C3AED)" }}>
            <Plane className="ch-anim absolute right-8 top-6 h-12 w-12 text-white/25" style={{ animation: "ch-float 6s ease-in-out infinite" }} />
            <div className="relative px-6 py-9 sm:px-10">
              <p className="text-[11px] font-extrabold tracking-[0.25em] uppercase text-cyan-100/80">✈ Earn Your Way To The World</p>
              <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-2 max-w-xl">Your Next Destination Could Be Your Next Achievement.</h3>
              <p className="text-sm text-white/75 mt-2">Keep building. Keep earning. Keep growing.</p>
            </div>
          </section>

          {/* REWARD SHOWCASE — only Root-configured rewards */}
          {rewards.length > 0 && (
            <section>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-5 flex items-center gap-2 text-zinc-900 dark:text-white"><Gift className="h-6 w-6" style={{ color: "#DB2777" }} /> UNLOCK BIGGER REWARDS</h2>
              <div data-testid="reward-showcase" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {rewards.map((r, i) => {
                  const RI = REWARD_ICON[r.type] || Gem;
                  return (
                    <div key={i} data-testid={`reward-${i}`} className="ch-card overflow-hidden">
                      {r.image ? <img src={fileUrl(r.image)} alt="" className="w-full h-32 object-cover" />
                        : <div className="h-24 flex items-center justify-center" style={{ background: CARD_GRADS[i % CARD_GRADS.length] }}><RI className="h-10 w-10 text-white/70" /></div>}
                      <div className="p-5">
                        <p className="font-display font-extrabold text-zinc-900">{r.name}</p>
                        <p className="text-xs text-zinc-500 mt-1">Unlock at {fmtINR(r.target)} challenge target</p>
                        <span className="mt-2 inline-block rounded-full bg-zinc-100 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-zinc-500">{r.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* UPCOMING */}
          {data.upcoming.length > 0 && (
            <section>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-5 flex items-center gap-2 text-zinc-900 dark:text-white"><CalendarDays className="h-6 w-6" style={{ color: "#2563EB" }} /> UPCOMING CHALLENGES</h2>
              <div className="grid md:grid-cols-2 gap-6">{data.upcoming.map((c, i) => <ChallengeCard key={c.id} c={c} onShare={share} idx={i + 1} />)}</div>
            </section>
          )}

          {/* TOP CHALLENGE PERFORMERS (reuses existing leaderboard aggregation) */}
          {board && board.length > 0 && (
            <section className="ch-card p-6">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h2 className="font-display font-extrabold text-2xl tracking-tight flex items-center gap-2 text-zinc-900"><Trophy className="h-6 w-6" style={{ color: "#F5B700" }} /> TOP CHALLENGE PERFORMERS</h2>
                <Link to="/partner/leaderboard" data-testid="view-top-freelancers" className="inline-flex items-center gap-1.5 text-xs font-extrabold" style={{ color: "#007A4D" }}>View Top Freelancers <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
              <div className="space-y-3">
                {board.slice(0, 3).map((r) => (
                  <div key={r.partner_id} data-testid={`performer-${r.rank}`} className="flex items-center gap-4 rounded-2xl border border-zinc-100 bg-white/70 p-3.5">
                    <span className="h-9 w-9 rounded-full flex items-center justify-center text-white font-display font-extrabold text-sm" style={{ background: MEDAL[r.rank] }}>{r.rank}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm text-zinc-900 truncate">{r.name}</p>
                      <p className="text-[11px] text-zinc-500">Last 30 days progress</p>
                    </div>
                    <p className="font-display font-extrabold text-zinc-900">{fmtINR(r.total)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ACHIEVEMENTS */}
          <section>
            <h2 className="font-display font-extrabold text-2xl tracking-tight mb-5 flex items-center gap-2 text-zinc-900 dark:text-white"><Medal className="h-6 w-6" style={{ color: "#F5B700" }} /> MY ACHIEVEMENTS</h2>
            {data.achievements.length === 0 ? (
              <div className="ch-card p-8 text-center">
                <Medal className="h-9 w-9 mx-auto text-zinc-300 mb-2" />
                <p className="text-sm text-zinc-500">Complete a challenge to earn your first achievement badge.</p>
              </div>
            ) : (
              <div data-testid="achievements-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {data.achievements.map((a) => {
                  const ts = TIER[a.tier] || TIER.gold;
                  return (
                    <div key={a.id} data-testid={`achievement-${a.id}`} className="ch-card p-5 border-l-4" style={{ borderLeftColor: ts.bd }}>
                      <span className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: ts.bg, color: ts.tx }}><Medal className="h-5 w-5" /></span>
                      <p className="font-display font-extrabold text-zinc-900 mt-3">{a.title}</p>
                      <p className="text-xs text-zinc-500 mt-1 capitalize">{a.tier} tier · {fmtINR(a.target)} target{a.reward ? ` · ${a.reward}` : ""}</p>
                      <p className="text-[11px] text-zinc-400 mt-1">Completed {fmtDateTime(a.completed_at).split(",")[0]}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* PAST */}
          {data.past.length > 0 && (
            <section>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-5 text-zinc-900 dark:text-white">Past Challenges</h2>
              <div className="grid md:grid-cols-2 gap-6">{data.past.map((c, i) => <ChallengeCard key={c.id} c={c} onShare={share} idx={i + 2} />)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
